import { useFeature } from "@hackler/react-sdk";
import { useRouter } from "next/router";

/**
 * 2026 추석 연휴(9/23 21:00 ~ 9/28 09:00) 콜 차단 스위치.
 *
 * goi-platform-web 의 platform, ssr-platform, parlor 에 같은 이름 훅이 있다.
 * 레포와 배포가 갈려 파일은 따로 두되 플래그 키와 FORCE 값은 항상 같이 맞춘다.
 * 한쪽만 켜지면 도메인별로 화면이 갈린다.
 *
 * 연휴에는 상담 인력이 최소로 돌아 전화를 받지 못한다. 못 받을 전화를 유도하면
 * 급한 상황에 건 사람에게 제일 나쁜 경험이 되므로, 콜 표면만 내리고
 * 폼(견적, 체크리스트)은 그대로 둔다. 폼은 연휴가 끝나고 처리해도 되기 때문이다.
 */

/**
 * 핵클 기능 플래그 키. ON 이면 차단이다.
 *
 * 독사운드 6개 포털은 goi-platform-web 과 다른 Hackle SDK 키를 쓴다.
 * 그쪽 워크스페이스는 30 번이고 여기는 4 번이다. 번호가 다른 것이 정상이다.
 *
 * heysangjo 는 이 레포에 있으면서 platform 과 같은 SDK 키를 쓴다. 다만 이 훅을 부르는
 * 컴포넌트(StepGuideFooter, FullWidthButton, FullWidthTwinButton)를 하나도 쓰지 않아
 * 지금은 영향이 없다. heysangjo 에 그 컴포넌트를 붙일 일이 생기면 키부터 다시 봐야 한다.
 */
const CHUSEOK_2026_FEATURE_KEY: number | null = 4;

/** 핵클 없이 강제로 켜는 스위치. 연휴에 핵클이 안 먹으면 이 줄만 true 로 바꿔 배포한다. */
const CHUSEOK_2026_FORCE = false;

export const useChuseokOff = (): boolean => {
  /** 훅은 조건부 호출이 안 되므로 키가 없을 때도 호출은 하되 결과를 무시한다. */
  const featureOn = useFeature(CHUSEOK_2026_FEATURE_KEY ?? 0);

  if (CHUSEOK_2026_FORCE) return true;
  if (CHUSEOK_2026_FEATURE_KEY === null) return false;

  return featureOn;
};

/**
 * 이번 차단 대상 경로.
 *
 * 포털 7개가 같은 화면을 서로 다른 URL 로 서비스해서 한 줄로 줄일 규칙이 없다.
 * 줄이려고 "facility" 같은 조각으로 매칭하면 장례식장 경로까지 걸리므로 전부 적는다.
 * 앱마다 배포 단위가 달라 다른 포털의 접두어가 섞여도 매칭될 일은 없다.
 *
 * 혜택 계열은 인덱스와 상세를 같이 덮도록 상위 접두어로 둔다.
 * 여기 없는 것은 장례식장 상세와 지역 목록뿐이다. 이번 차단 대상이 아니다.
 */
const CHUSEOK_OFF_PATH_PREFIXES = [
  /** 혜택 (인덱스 + 장지, 화장장 상세) */
  "/benefit/",
  "/veterans/",
  "/national/",
  "/national-hero/",
  "/national-merit/",
  "/free-benefit/",
  "/free-national/",
  "/support/",
  "/benefit-support/",
  "/beneficiary/",
  "/gangwon-benefit/",
  "/gangwon-veterans/",
  /** 장지 상세 */
  "/facility-place/",
  "/facility-item/",
  "/facility-company/",
  "/facility-recommend-item/",
  "/gangwon-facility/",
  /** 지역 화장장 목록 */
  "/crematorium-place-list/region/",
  "/crematorium-price/region/",
  "/crematorium-list/region/",
  "/crematorium-recommend-list/region/",
  "/gangwon-crematorium-place/region/",
  "/crematorium-place/region/",
  /** 장례식장 + 화장장 + 장지 통합 추천 */
  "/funeral-recommend/",
  "/parlor-crema-facility/",
  "/all-process-recommend/",
  "/funeral-process-recommend/",
  "/gangwon-all-funeral/",
  "/all-funeral/",
];

export const isChuseokOffPath = (asPath: string): boolean => {
  const path = asPath.split("?")[0];

  /** 포털 홈. 접두어로 두면 전 페이지가 걸리므로 정확히 일치할 때만 본다. */
  if (path === "/" || path === "") return true;

  return CHUSEOK_OFF_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

/** 지금 이 화면에서 고이 대표번호로 거는 표면을 내려야 하는지. */
export const useIsChuseokCallOff = (): boolean => {
  const router = useRouter();
  const isChuseokOff = useChuseokOff();

  return isChuseokOff && isChuseokOffPath(router.asPath);
};
